import { getGradeTrackProgramPreferences } from '../services/sectionLayoutService';
import { compactAnnualProgressDataCachesForWorkspace } from '../utils/annualProgressDataCache';
import { reseedGradeTrackProgramPrefsLocalStores } from '../utils/gradeTrackProgramPrefsLocalSeed';
import {
  readStoredSchoolDataRevisions,
  syncSchoolScopedDataCachesDetailed,
} from '../utils/schoolDataCacheVersion';
import { isAffectedBySchoolDataDomains, schoolQueryKeyDomain } from '../utils/schoolDataDomains';
import { getSchoolDataLoginHeadSyncGeneration } from '../utils/schoolDataLoginHeadSync';
import { schoolKeys } from './keys';
import { queryClient } from './queryClient';

/** 로그인/새로고침 generation당 soft invalidate 1회 */
let lastSoftInvalidateGeneration = -1;

/**
 * 편제 prefs가 바뀐 경우: mode/sync/hoursAdjust LS 거울은 purge 대상이 아니므로(풀림 방지)
 * 백그라운드에서 DB prefs를 받아 엄격 재시드한다. 바뀐 값이 있으면 TRACK_PROGRAM_SPLIT 이벤트로 화면이 갱신된다.
 */
function refreshGradeTrackPrefsMirrors(workspaceId: string): void {
  void getGradeTrackProgramPreferences(workspaceId, { skipLocalCache: true })
    .then((prefs) => {
      reseedGradeTrackProgramPrefsLocalStores(workspaceId, prefs);
    })
    .catch(() => {
      /* 다음 로드에서 재시도 */
    });
}

/**
 * `workspaces.data_revisions`(파트별) 동기화 + 바뀐 파트의 React Query 캐시만 제거.
 * - 파트별 revision 사용 가능: 요청 1회로 전 파트를 비교하므로, 일치하면 테이블 head 비교 생략,
 *   다르면 바뀐 파트만 제거(나머지 쿼리는 그대로 캐시 사용)
 * - revision 미지원(마이그레이션 전): 로그인·새로고침 generation당 1회 soft invalidate로 테이블 head 비교
 * @returns true면 로컬·원격 버전 일치, false면 바뀐 파트 LS purge·RQ remove 완료.
 */
export async function syncSchoolCachesWithQuery(workspaceId: string): Promise<boolean> {
  compactAnnualProgressDataCachesForWorkspace(workspaceId);
  const { isFresh, changedDomains } = await syncSchoolScopedDataCachesDetailed(workspaceId);
  const perDomainRevisions = readStoredSchoolDataRevisions(workspaceId) != null;
  if (!isFresh) {
    if (changedDomains === 'all') {
      await queryClient.invalidateQueries({ queryKey: schoolKeys.all(workspaceId) });
      queryClient.removeQueries({ queryKey: schoolKeys.all(workspaceId) });
    } else {
      const changed = new Set(changedDomains);
      const predicate = (query: { queryKey: readonly unknown[] }) =>
        isAffectedBySchoolDataDomains(schoolQueryKeyDomain(query.queryKey), changed);
      if (perDomainRevisions) {
        /** 화면에 떠 있는 바뀐 파트 쿼리는 다시 받고(LS는 이미 purge), 나머지 바뀐 파트는 제거 */
        void queryClient.invalidateQueries({ queryKey: schoolKeys.all(workspaceId), predicate });
        queryClient.removeQueries({ queryKey: schoolKeys.all(workspaceId), predicate, type: 'inactive' });
      } else {
        queryClient.removeQueries({ queryKey: schoolKeys.all(workspaceId), predicate });
        await queryClient.invalidateQueries({ queryKey: schoolKeys.all(workspaceId) });
      }
    }
    lastSoftInvalidateGeneration = getSchoolDataLoginHeadSyncGeneration();
    if (changedDomains === 'all' || changedDomains.includes('gradeTrackPrefs')) {
      refreshGradeTrackPrefsMirrors(workspaceId);
    }
  } else if (!perDomainRevisions) {
    const gen = getSchoolDataLoginHeadSyncGeneration();
    if (gen > lastSoftInvalidateGeneration) {
      lastSoftInvalidateGeneration = gen;
      await queryClient.invalidateQueries({ queryKey: schoolKeys.all(workspaceId) });
    }
  }
  return isFresh;
}
